import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import {
  type NotificationRule,
  fetchNotificationTypes,
  fetchNotificators,
  createNotification,
  updateNotification,
} from '@/lib/traccar-api';

interface NotificationRuleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rule?: NotificationRule;
}

export function NotificationRuleDialog({ open, onOpenChange, rule }: NotificationRuleDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [type, setType] = useState('');
  const [always, setAlways] = useState(false);
  const [selectedNotificators, setSelectedNotificators] = useState<string[]>([]);
  const [minIdleMinutes, setMinIdleMinutes] = useState<string>('');

  useEffect(() => {
    if (open) {
      setType(rule?.type ?? '');
      setAlways(rule?.always ?? false);
      setSelectedNotificators(rule?.notificators ? rule.notificators.split(',').filter(Boolean) : []);
      const minIdle = rule?.attributes?.minIdlePeriod as number | undefined;
      setMinIdleMinutes(minIdle ? String(Math.round(minIdle / 60)) : '');
    }
  }, [open, rule]);

  const KNOWN_TYPES = [
    'alarm', 'commandResult', 'deviceFuelDrop', 'deviceFuelIncrease',
    'deviceIdle', 'deviceInactive', 'deviceMoving', 'deviceOffline',
    'deviceOnline', 'deviceOverspeed', 'deviceStopped', 'deviceUnknown',
    'driverChanged', 'geofenceEnter', 'geofenceExit', 'ignitionOff',
    'ignitionOn', 'maintenance', 'media', 'queuedCommandSent', 'test', 'textMessage',
  ];

  const { data: apiTypes = [] } = useQuery({
    queryKey: ['notificationTypes'],
    queryFn: fetchNotificationTypes,
  });

  const types = [
    ...apiTypes,
    ...KNOWN_TYPES
      .filter((k) => !apiTypes.some((t) => t.type === k))
      .map((k) => ({ type: k })),
  ];

  const { data: notificators = [] } = useQuery({
    queryKey: ['notificators'],
    queryFn: fetchNotificators,
  });

  const mutation = useMutation({
    mutationFn: (data: Omit<NotificationRule, 'id'>) =>
      rule ? updateNotification({ ...data, id: rule.id }) : createNotification(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      toast.success(rule ? t('notifications.config.updated') : t('notifications.config.created'));
      onOpenChange(false);
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const attributes: Record<string, unknown> = { ...(rule?.attributes ?? {}) };
    if (type === 'deviceIdle' && minIdleMinutes) {
      attributes.minIdlePeriod = parseInt(minIdleMinutes, 10) * 60;
    } else {
      delete attributes.minIdlePeriod;
    }
    mutation.mutate({
      type,
      always,
      notificators: selectedNotificators.join(','),
      calendarId: rule?.calendarId ?? 0,
      attributes,
    });
  };

  const toggleNotificator = (notificatorType: string) => {
    setSelectedNotificators((prev) =>
      prev.includes(notificatorType)
        ? prev.filter((n) => n !== notificatorType)
        : [...prev, notificatorType]
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {rule ? t('notifications.config.editTitle') : t('notifications.config.createTitle')}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="type">{t('notifications.config.type')}</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger id="type">
                <SelectValue placeholder={t('notifications.config.type')} />
              </SelectTrigger>
              <SelectContent>
                {[...types]
                  .sort((a, b) =>
                    t(`notifications.events.${a.type}`, { defaultValue: a.type }).localeCompare(
                      t(`notifications.events.${b.type}`, { defaultValue: b.type })
                    )
                  )
                  .map((nt) => (
                    <SelectItem key={nt.type} value={nt.type}>
                      {t(`notifications.events.${nt.type}`, { defaultValue: nt.type })}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          {type === 'deviceIdle' && (
            <div className="space-y-2">
              <Label htmlFor="minIdleMinutes">{t('notifications.config.idleTime')}</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="minIdleMinutes"
                  type="number"
                  min="1"
                  value={minIdleMinutes}
                  onChange={(e) => setMinIdleMinutes(e.target.value)}
                  placeholder="5"
                  className="w-24"
                />
                <span className="text-sm text-muted-foreground">{t('notifications.config.idleTimeUnit')}</span>
              </div>
              <p className="text-xs text-muted-foreground">{t('notifications.config.idleTimeDesc')}</p>
            </div>
          )}

          {notificators.length > 0 && (
            <div className="space-y-2">
              <Label>{t('notifications.config.channels')}</Label>
              <div className="flex flex-wrap gap-4">
                {notificators.map((n) => (
                  <div key={n.type} className="flex items-center gap-2">
                    <Checkbox
                      id={`notificator-${n.type}`}
                      checked={selectedNotificators.includes(n.type)}
                      onCheckedChange={() => toggleNotificator(n.type)}
                    />
                    <Label htmlFor={`notificator-${n.type}`} className="capitalize cursor-pointer">
                      {n.type}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between rounded-lg border p-3">
            <div className="space-y-0.5">
              <Label>{t('notifications.config.always')}</Label>
              <p className="text-xs text-muted-foreground">{t('notifications.config.alwaysDesc')}</p>
            </div>
            <Switch checked={always} onCheckedChange={setAlways} />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={!type || mutation.isPending}>
              {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {rule ? t('common.save') : t('common.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
